import {
  Component,
  ExpressionPath,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {
  EElement,
  ERecord,
  EList,
  ELiteral,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  getPageComponentRender,
  isUserComponentInst,
  pageComponentType,
  updateComponentRenderInComponentMap,
  findElement,
} from '@packages/utilities/platform';
import {GuidGenerator} from '@packages/utilities/guid';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {BASE_VARIANT_DEFAULTS} from '@packages/systems/component-style-variants';
import {
  isElementBaseFrame,
  getVariantInstanceElementFromFrame,
  getFrameElementFromVariantId,
  getBaseVariantIdForNativeComponent,
  type GetBaseVariantId,
} from './frameUtils';
import {
  isFrameElement,
  isFrameLayoutElement,
} from '@packages/utilities/elementTypeUtils';
import uuidv5 from 'uuid/v5';
import {ComponentMapOperation} from '@packages/utilities/multiplayer-utils';
import type {ComponentStateScenario} from '@packages/systems/code-libraries/component-states';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

const SCENARIO_FRAME_NAMESPACE = 'a8c7e1c2-1b6f-4f3c-bef6-7e1d0f4f8c01';

const CHILDREN_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

/**
 * Function for generating deterministic GUIDs for frame and component instance
 * elements given an `initialId`.
 *
 * This function is used when new frame and instance elements are created during
 * component page sync and ensures the same input states result in the same
 * output expression for `page.render` in the component map.
 */
function generateDeterministicIds(initialId: string) {
  const guidGenerator = new GuidGenerator(initialId);

  return {
    frameId: guidGenerator.getNext(),
    instanceId: guidGenerator.getNext(),
  };
}

/**
 * Used to create deterministic GUIDs for Base frame and instance elements
 * where the `initialId` to increment is a deterministic UUID generated from
 * the Base variant's ID and Component ID via uuidv5.
 *
 * The use of uuidv5 to create the `initialId` avoids potential ID collisions with
 * elements in the `component.render` tree. This is because some element types with children
 * that can be bound to props have new IDs created during component creation, which increments
 * each new element ID from the component root's ID value.
 */
function generateDeterministicBaseIds(componentId: string) {
  const initialId = uuidv5(BASE_VARIANT_DEFAULTS.id, componentId);
  return generateDeterministicIds(initialId);
}

/**
 * Utility for updating component page content to contain Frame elements that reflect
 * the current state of `component.variants` for a User component with the provided `componentId`.
 *
 * When `ff-aicc-visual-editing-states` is on and the component has stored multi-state
 * preview scenarios (`source.meta.states`), emits one frame per (variant × scenario)
 * cell so resync paths — page switch, variant edits, multiplayer, Designer Extensions —
 * cannot collapse the state axis. Flag off (or no stored scenarios) keeps the plain
 * variant-frame layout — matching `selectScenariosForComponent` — so leftover states
 * cannot recreate unbound grid frames while layout/bindings stay single-axis.
 *
 * Prefer this at call sites. The only production exception is `CODE_EVALUATED`,
 * which calls `syncComponentPageGridFrames` with the flag-gated selector. Server-side
 * callers (multiplayer) should use `syncComponentPageGridFrames` with the server flag
 * rather than this client-flagged helper.
 */
export function syncComponentPageFrames(
  componentMap: Component.ComponentMap,
  componentId: string
): Component.ComponentMap {
  if (!getFeatureFlagOutsideOfReact(FEATURE_FLAGS.AICC_VISUAL_EDITING_STATES)) {
    return syncComponentPageVariantFrames(componentMap, componentId);
  }

  const component = componentMap.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  const scenarios = component
    ? Component.getPreviewScenarios(component)
    : undefined;
  if (scenarios && scenarios.length > 0) {
    return syncComponentPageGridFrames(componentMap, componentId, scenarios);
  }
  return syncComponentPageVariantFrames(componentMap, componentId);
}

/**
 * Variant-only frame sync (one Frame per style variant). Used when the
 * component has no stored multi-state scenarios, and as the empty-scenario
 * fallback from `syncComponentPageGridFrames`.
 */
function syncComponentPageVariantFrames(
  componentMap: Component.ComponentMap,
  componentId: string
): Component.ComponentMap {
  const pageRender = getPageComponentRender(componentMap);
  const pageElement = pageRender ? Expressions.getElement(pageRender) : null;
  const frameLayoutElement = pageElement
    ? findElement(pageElement, isFrameLayoutElement)
    : null;
  const existingChildren = frameLayoutElement
    ? Expressions.getIn(
        Expressions.getElementData(frameLayoutElement),
        CHILDREN_PATH
      )
    : null;
  const existingFrames = existingChildren
    ? Expressions.getList(existingChildren) || []
    : [];
  const component = componentMap.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  if (!component || !pageElement || !pageRender || !frameLayoutElement) {
    return componentMap;
  }

  const variants = Component.getVariants(component);
  const variantGroupIds = variants ? Object.keys(variants) : null;
  const variantGroupId = variantGroupIds ? variantGroupIds[0] : null;
  if (!variantGroupId || !variants) {
    return handleNoVariantsCase(
      existingFrames,
      componentId,
      componentMap,
      frameLayoutElement,
      pageElement,
      pageRender
    );
  }
  const variantList = variantGroupId ? variants[variantGroupId] : null;

  if (!variantList) {
    return componentMap;
  }

  const existingFramesVariantIdMap = getExistingFramesVariantIdMap(
    existingFrames,
    variantGroupId,
    componentMap
  );

  const updatedFrames = updateFrames(
    variantList,
    existingFramesVariantIdMap,
    existingFrames,
    componentId,
    variantGroupId,
    componentMap
  );

  return updatePageWithChildren(
    updatedFrames,
    frameLayoutElement,
    pageElement,
    pageRender,
    componentMap
  );
}

function updateFrames(
  variantList: Component.StyleVariant[],
  existingFramesVariantIdMap: Map<string, string>,
  existingFrames: Expression[],
  componentId: string,
  variantGroupId: string,
  componentMap: Component.ComponentMap
) {
  return variantList.map((variant: Component.StyleVariant) => {
    const existingFrameId = existingFramesVariantIdMap.get(variant.id);

    if (existingFrameId) {
      // Reuse existing Frame element for this variant
      const existingFrame = existingFrames.find((frame) => {
        const frameElement = Expressions.getElement(frame);
        return frameElement?.id === existingFrameId;
      });

      if (existingFrame) {
        if (variant.id === BASE_VARIANT_DEFAULTS.id) {
          return ensureBaseFrameHasVariantProp(
            existingFrame,
            variantGroupId,
            componentMap
          );
        }

        return existingFrame;
      }
    }

    const {frameId, instanceId} =
      variant.id === BASE_VARIANT_DEFAULTS.id
        ? generateDeterministicBaseIds(componentId)
        : generateDeterministicIds(variant.id);

    // Create component instance with variant override
    const componentInstance = EElement({
      id: instanceId,
      type: qualify(Plugins.name('__SitePlugin'), Component.name(componentId)),
      data: ERecord({
        [variantGroupId]: ELiteral({
          name: qualify(Plugins.name('Basic'), typeName('StyleVariant')),
          value: variant.id,
        }),
      }),
    });

    const baseFrame =
      existingFrames.find((frame) => isBaseFrame(frame, componentMap)) ??
      existingFrames[0] ??
      null;

    const baseFrameElement = baseFrame
      ? Expressions.getElement(baseFrame)
      : null;

    const baseFrameData = baseFrameElement
      ? Expressions.getElementData(baseFrameElement)
      : null;

    const baseFrameSize = baseFrameData
      ? Expressions.getIn(baseFrameData, [{in: 'Record', at: 'size'}])
      : null;

    const baseFrameBackground = baseFrameData
      ? Expressions.getIn(baseFrameData, [{in: 'Record', at: 'background'}])
      : null;

    // Create Frame element containing the component instance
    // New frames inherit their settings from the base frame
    const frameElement = EElement({
      id: frameId,
      type: qualify(Plugins.name('Frame'), Component.name('Frame')),
      data: ERecord({
        children: EList([componentInstance]),
        ...(baseFrameSize ? {size: baseFrameSize} : {}),
        ...(baseFrameBackground ? {background: baseFrameBackground} : {}),
      }),
    });

    return frameElement;
  });
}

function getExistingFramesVariantIdMap(
  existingFrames: Expression[],
  variantGroupId: string,
  componentMap: Component.ComponentMap
) {
  return existingFrames.reduce((acc, frame) => {
    const frameElement = Expressions.getElement(frame);
    if (!frameElement || !isFrameElement(frameElement)) {
      return acc;
    }

    const existingFrameChildren = Expressions.getIn(
      Expressions.getElementData(frameElement),
      CHILDREN_PATH
    );

    // Get the first element from the children list
    const childrenList = existingFrameChildren
      ? Expressions.getList(existingFrameChildren)
      : null;
    const componentInstanceElement = childrenList?.[0]
      ? Expressions.getElement(childrenList[0])
      : null;

    if (
      !componentInstanceElement ||
      !isUserComponentInst(componentMap, componentInstanceElement)
    ) {
      return acc;
    }

    const componentInstanceData = Expressions.getElementData(
      componentInstanceElement
    );

    const componentInstanceVariantData = Expressions.getIn(
      componentInstanceData,
      [{in: 'Record', at: variantGroupId}]
    );

    if (!componentInstanceVariantData) {
      // It's possible for the Base frame's component instance element
      // to have missing or stale variant prop overrides in certain scenarios,
      // such as when all non-base variants are deleted and then new one are added.
      // This check ensures the Base frame is included in the Map in such a scenario.
      if (isElementBaseFrame(frameElement, componentMap)) {
        acc.set(BASE_VARIANT_DEFAULTS.id, frameElement.id);
      }

      return acc;
    }

    const variantId = Expressions.getLiteral(
      componentInstanceVariantData
    )?.value;

    if (!variantId || typeof variantId !== 'string') {
      return acc;
    }

    acc.set(variantId, frameElement.id);
    return acc;
  }, new Map<string, string>());
}

/**
 * Checks if a frame contains a component instance with no variant overrides or with the base variant override
 */
function isBaseFrame(
  frame: Expression,
  componentMap: Component.ComponentMap
): boolean {
  const frameElement = Expressions.getElement(frame);
  if (!frameElement) return false;
  return isElementBaseFrame(frameElement, componentMap);
}

/**
 * Creates a new base Frame element containing a component instance
 */
function createBaseFrameElement(componentId: string): Expression {
  const {frameId, instanceId} = generateDeterministicBaseIds(componentId);

  return EElement({
    id: frameId,
    type: qualify(Plugins.name('Frame'), Component.name('Frame')),
    data: ERecord({
      children: EList([
        EElement({
          id: instanceId,
          type: qualify(
            Plugins.name('__SitePlugin'),
            Component.name(componentId)
          ),
          data: ERecord({}),
        }),
      ]),
    }),
  });
}

function updatePageWithChildren(
  children: Expression[],
  frameLayoutElement: ExpressionElement,
  pageElement: ExpressionElement,
  pageRender: Expression,
  componentMap: Component.ComponentMap
): Component.ComponentMap {
  const updatedFrameLayoutData = Expressions.sparseSetIn(
    Expressions.getElementData(frameLayoutElement),
    CHILDREN_PATH,
    EList(children)
  );

  const updatedPageRender = Expressions.updateElement(
    frameLayoutElement.id,
    (el) => {
      return Expressions.setElementData(el, updatedFrameLayoutData);
    },
    pageRender
  );

  return updateComponentRenderInComponentMap(
    componentMap,
    pageComponentType,
    updatedPageRender
  );
}

/**
 * Handles the case where there are no variants for the component
 */
function handleNoVariantsCase(
  existingFrames: Expression[],
  componentId: string,
  componentMap: Component.ComponentMap,
  frameLayoutElement: ExpressionElement,
  pageElement: ExpressionElement,
  pageRender: Expression
): Component.ComponentMap {
  const existingBaseFrame = existingFrames.find((frame) =>
    isBaseFrame(frame, componentMap)
  );
  return updatePageWithChildren(
    [existingBaseFrame || createBaseFrameElement(componentId)],
    frameLayoutElement,
    pageElement,
    pageRender,
    componentMap
  );
}

/**
 * Ensures a base frame's component instance has the variant prop set to BASE_VARIANT_DEFAULTS.id.
 * This is needed when reusing a base frame that was created before any variants existed.
 */
function ensureBaseFrameHasVariantProp(
  baseFrame: Expression,
  variantGroupId: string,
  componentMap: Component.ComponentMap
): Expression {
  const baseFrameElement = Expressions.getElement(baseFrame);
  if (!baseFrameElement) return baseFrame;

  const instanceElement = getVariantInstanceElementFromFrame(
    baseFrameElement,
    componentMap
  );
  if (!instanceElement) return baseFrame;

  // Check if variant prop already exists
  const existingVariantData = Expressions.getIn(
    Expressions.getElementData(instanceElement),
    [{in: 'Record', at: variantGroupId}]
  );

  // Already has variant prop - return as-is
  if (existingVariantData) return baseFrame;

  // Otherwise, add variant prop to child component instance
  const updatedInstance = {
    ...instanceElement,
    data: ERecord({
      [variantGroupId]: ELiteral({
        name: qualify(Plugins.name('Basic'), typeName('StyleVariant')),
        value: BASE_VARIANT_DEFAULTS.id,
      }),
    }),
  };

  const updatedFrameData = Expressions.sparseSetIn(
    Expressions.getElementData(baseFrameElement),
    [{in: 'Record', at: 'children'}],
    EList([EElement(updatedInstance)])
  );

  return EElement(
    Expressions.setElementData(baseFrameElement, updatedFrameData)
  );
}

/**
 * Sentinel used in the deterministic id seed for components that declare
 * no style variants — the grid then has a single implicit variant track.
 */
const GRID_NO_VARIANT_KEY = '__base__';

/**
 * Builds deterministic frame and instance ids for the per-cell frames
 * emitted by `syncComponentPageGridFrames`. A cell is the cross product
 * of one style `variantId` (or `null` when the component has no variants)
 * and one `withStates` `scenarioId`. Namespaced under a scenario-specific
 * UUID so the ids do not collide with the deterministic variant-frame ids
 * generated from BASE_VARIANT_DEFAULTS.
 *
 * Determinism matters because the side-channel scenario binding store
 * (`CodeIslandCustomElementStore.scenarioBindings`) keys by instance id —
 * if ids drifted across re-syncs the bindings would orphan.
 */
function generateDeterministicGridIds(
  componentId: string,
  variantId: string | null,
  scenarioId: string
) {
  const variantKey = variantId ?? GRID_NO_VARIANT_KEY;
  const initialId = uuidv5(
    `grid:${variantKey}:${scenarioId}`,
    uuidv5(componentId, SCENARIO_FRAME_NAMESPACE)
  );
  return generateDeterministicIds(initialId);
}

/**
 * Returns the deterministic instance id that `syncComponentPageGridFrames`
 * assigns to the component instance inside the frame for the given
 * `(componentId, variantId, scenarioId)` cell. Used by the binding-sync
 * engine to map each frame's instance back to its scenario without
 * re-walking the WFDL tree.
 */
export function getGridFrameInstanceId(
  componentId: string,
  variantId: string | null,
  scenarioId: string
): string {
  return generateDeterministicGridIds(componentId, variantId, scenarioId)
    .instanceId;
}

/**
 * Returns the deterministic Frame element id that
 * `syncComponentPageGridFrames` assigns to the `(componentId, variantId,
 * scenarioId)` cell. Used by the Navigator panel's States list to dispatch
 * `componentCanvasFrameSelected` with the right frame id without scanning
 * the WFDL tree.
 */
export function getGridFrameId(
  componentId: string,
  variantId: string | null,
  scenarioId: string
): string {
  return generateDeterministicGridIds(componentId, variantId, scenarioId)
    .frameId;
}

/**
 * Returns the ordered list of style variant ids for a component, or
 * `[null]` when the component declares no variants (a single implicit
 * variant track). Shared by `syncComponentPageGridFrames`, the
 * scenario-binding engine, and the frame-tools/breadcrumb resolvers so
 * they all agree on which `(variant, scenario)` cells exist.
 */
export function getGridVariantIds(
  componentMap: Component.ComponentMap,
  componentId: string
): Array<string | null> {
  const component = componentMap.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  if (!component) return [null];
  const variants = Component.getVariants(component);
  const variantGroupId = variants ? Object.keys(variants)[0] : null;
  const variantList =
    variantGroupId && variants ? variants[variantGroupId] : null;
  return toGridVariantIds(variantList);
}

/**
 * Normalizes an already-resolved style-variant list to the grid's variant
 * tracks: the list of variant ids, or `[null]` (a single implicit track)
 * when the component declares no variants. Use this when you already hold
 * the style variants (e.g. from `selectComponentPageStyleVariants`); use
 * `getGridVariantIds` when you only have the component map.
 */
export function toGridVariantIds(
  styleVariants: ReadonlyArray<{id: string}> | null | undefined
): Array<string | null> {
  return styleVariants && styleVariants.length > 0
    ? styleVariants.map((variant) => variant.id)
    : [null];
}

/** A single `(variant × scenario)` cell of the multi-state preview grid. */
export type GridCell = {
  /** Style-variant id for this cell, or `null` for the implicit track. */
  variantId: string | null;
  /** The scenario rendered in this cell. */
  scenario: ComponentStateScenario;
  /** Deterministic id of the cell's `Frame` element (selection / breadcrumb). */
  frameId: string;
  /** Deterministic id of the cell's component instance (scenario bindings). */
  instanceId: string;
};

/**
 * Enumerates the `(variant × scenario)` grid cells for a component, pairing
 * each with the deterministic ids `syncComponentPageGridFrames` emits.
 *
 * This is the single source of truth for "which cells exist and what ids
 * they have", shared by the frame generator, the scenario-binding engine,
 * the frame-tools / breadcrumb resolvers, and the Navigator States list so
 * they cannot drift. Cells are returned variant-major / scenario-minor to
 * match the emitted frame order.
 */
export function getGridCells(
  componentId: string,
  variantIds: ReadonlyArray<string | null>,
  scenarios: readonly ComponentStateScenario[]
): GridCell[] {
  const cells: GridCell[] = [];
  for (const variantId of variantIds) {
    for (const scenario of scenarios) {
      const {frameId, instanceId} = generateDeterministicGridIds(
        componentId,
        variantId,
        scenario.id
      );
      cells.push({variantId, scenario, frameId, instanceId});
    }
  }
  return cells;
}

/**
 * Reverse of the grid emitter: given the id of a Frame in the component
 * page, returns the multi-state preview scenario id that produced it, or
 * `null` when `frameId` isn't a `(variant × scenario)` grid cell (a plain
 * variant frame, or a component with no stored states).
 *
 * The scenario identity isn't stored on the frame (it rides the
 * side-channel), so we recover it by matching against the same
 * `getGridCells` enumeration `syncComponentPageGridFrames` emits from —
 * which is why we can't just read the variant off the frame: variant-less
 * components use the `null` track (`__base__`), not `BASE_VARIANT_DEFAULTS.id`.
 */
export function getScenarioIdForGridFrame(
  componentMap: Component.ComponentMap,
  componentId: string,
  frameId: string
): string | null {
  const component = componentMap.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  const scenarios = component
    ? Component.getPreviewScenarios(component)
    : undefined;
  if (!scenarios || scenarios.length === 0) return null;

  const cell = getGridCells(
    componentId,
    getGridVariantIds(componentMap, componentId),
    scenarios
  ).find((gridCell) => gridCell.frameId === frameId);
  return cell?.scenario.id ?? null;
}

/**
 * Resolves the Frame id to select when switching to `targetVariantId`, while
 * preserving the multi-state preview scenario the user is currently focused
 * on.
 *
 * In the `(variant × scenario)` grid, naively selecting the variant's frame
 * drops the active scenario — switching variants while looking at "Step 2"
 * would snap back to the base scenario. When the currently selected frame is a
 * grid cell, this maps it to the same scenario under the new variant;
 * otherwise (no states, or the current selection isn't a grid cell) it falls
 * back to the plain variant frame.
 */
export function getVariantFrameIdPreservingScenario(
  componentId: string | null | undefined,
  targetVariantId: string,
  currentSelectedFrameId: string | null,
  componentMap: Component.ComponentMap,
  getBaseVariantId: GetBaseVariantId = getBaseVariantIdForNativeComponent
): string | null {
  const fallbackId =
    getFrameElementFromVariantId(
      targetVariantId,
      componentMap,
      getBaseVariantId
    )?.id ?? null;
  if (!componentId || !currentSelectedFrameId) return fallbackId;

  const component = componentMap.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  if (!component) return fallbackId;

  const scenarios = Component.getPreviewScenarios(component);
  if (!scenarios || scenarios.length === 0) return fallbackId;

  const gridVariantIds = getGridVariantIds(componentMap, componentId);
  const cells = getGridCells(componentId, gridVariantIds, scenarios);
  const current = cells.find((cell) => cell.frameId === currentSelectedFrameId);
  if (!current) return fallbackId;

  // Components with no style variants render their scenarios on the single
  // implicit `null` track, but the breadcrumb still targets the synthetic
  // base variant id. Normalize to the `null` track so the scenario match
  // below resolves instead of falling back to the first scenario frame.
  const hasStyleVariants = gridVariantIds.some((id) => id !== null);
  const targetTrackId = hasStyleVariants ? targetVariantId : null;

  // Same scenario, new variant track.
  const target = cells.find(
    (cell) =>
      cell.scenario.id === current.scenario.id &&
      cell.variantId === targetTrackId
  );
  return target?.frameId ?? fallbackId;
}

/**
 * Deterministic Frame id that `syncComponentPageFrames` assigns to a plain
 * (non-grid) variant track. Used to recover the active variant after
 * `syncComponentPageGridFrames` replaces plain frames with grid cells while
 * the canvas store still holds the removed plain-frame id.
 */
function getPlainVariantFrameId(
  componentId: string,
  variantId: string | null
): string {
  // The implicit `null` track and the synthetic Base variant share the same
  // deterministic base-frame id generator as `syncComponentPageFrames`.
  if (variantId === null || variantId === BASE_VARIANT_DEFAULTS.id) {
    return generateDeterministicBaseIds(componentId).frameId;
  }
  return generateDeterministicIds(variantId).frameId;
}

/**
 * Resolves the Frame id to select when switching to `targetScenarioId`, while
 * preserving the style-variant track the user is currently focused on.
 *
 * Reciprocal of `getVariantFrameIdPreservingScenario`: in the
 * `(variant × scenario)` grid, naively selecting the first variant's cell for
 * a scenario would drop the active variant — switching states while looking
 * at "Hover" would snap back to Base. When the currently selected frame is a
 * grid cell, this maps it to the same variant under the new scenario.
 *
 * When the store still holds a plain (pre-grid) variant frame id — the case
 * right after states are first generated and plain frames are replaced —
 * recover the variant via the deterministic plain-frame id and map to that
 * track's target-scenario cell. Otherwise fall back to the first variant
 * track.
 */
export function getScenarioFrameIdPreservingVariant(
  componentId: string | null | undefined,
  targetScenarioId: string,
  currentSelectedFrameId: string | null,
  componentMap: Component.ComponentMap
): string | null {
  if (!componentId) return null;

  const gridVariantIds = getGridVariantIds(componentMap, componentId);
  const fallbackId = getGridFrameId(
    componentId,
    gridVariantIds[0] ?? null,
    targetScenarioId
  );
  if (!currentSelectedFrameId) return fallbackId;

  const component = componentMap.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  if (!component) return fallbackId;

  const scenarios = Component.getPreviewScenarios(component);
  if (!scenarios || scenarios.length === 0) return fallbackId;

  const cells = getGridCells(componentId, gridVariantIds, scenarios);
  const current = cells.find((cell) => cell.frameId === currentSelectedFrameId);
  if (current) {
    // Same variant track, new scenario.
    const target = cells.find(
      (cell) =>
        cell.scenario.id === targetScenarioId &&
        cell.variantId === current.variantId
    );
    return target?.frameId ?? fallbackId;
  }

  // Plain-frame → grid transition: match the retained plain-frame id against
  // each track's deterministic plain frame id so we keep the active variant.
  for (const variantId of gridVariantIds) {
    if (
      getPlainVariantFrameId(componentId, variantId) === currentSelectedFrameId
    ) {
      return getGridFrameId(componentId, variantId, targetScenarioId);
    }
  }

  return fallbackId;
}

/**
 * Variant of `syncComponentPageFrames` that emits one Frame per
 * `(style variant × scenario)` cell, forming a 2D grid: style
 * variants run along one axis and stored scenarios along the other (see
 * `crossAxisTrackCount` in `getGridFrameLayoutCSS`).
 *
 * Prefer `syncComponentPageFrames` at call sites — it reads stored scenarios
 * from the component when the client flag is on. Exported for `CODE_EVALUATED`
 * (flag-gated selector) and the multiplayer-server variant sync (server flag).
 *
 * Frames are emitted variant-major / scenario-minor so that, combined with
 * a `crossAxisTrackCount` equal to the scenario count, the CSS grid wraps each
 * variant onto its own row (vertical orientation) or column (horizontal).
 *
 * Each cell instance carries the WFDL style-variant binding (so it renders
 * that variant's styles) while the scenario identity is carried out-of-band
 * via the cross-window `CodeIslandCustomElementStore.scenarioBindings` map,
 * which `CodeIsland` reads to write `data-wf-scenario-id` /
 * `data-wf-scenario-label` onto the host. The host's runtime then mounts
 * the user component once with the scenario's seeded props on top of the
 * variant styling.
 *
 * `scenarios` is expected to already be de-duped by id and capped (the shape
 * `extractComponentStateScenarios` produces): each `(variantId, scenarioId)`
 * pair maps to one deterministic frame id, so duplicate ids would collide.
 */
export function syncComponentPageGridFrames(
  componentMap: Component.ComponentMap,
  componentId: string,
  scenarios: readonly ComponentStateScenario[]
): Component.ComponentMap {
  if (scenarios.length === 0) {
    // No declared scenarios → fall back to the plain variant-frame layout.
    return syncComponentPageVariantFrames(componentMap, componentId);
  }

  const pageRender = getPageComponentRender(componentMap);
  const pageElement = pageRender ? Expressions.getElement(pageRender) : null;
  const frameLayoutElement = pageElement
    ? findElement(pageElement, isFrameLayoutElement)
    : null;
  const component = componentMap.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  if (!component || !pageElement || !pageRender || !frameLayoutElement) {
    return componentMap;
  }

  const variants = Component.getVariants(component);
  const variantGroupId = variants ? Object.keys(variants)[0] : null;
  const variantIds = getGridVariantIds(componentMap, componentId);

  const existingChildren = Expressions.getIn(
    Expressions.getElementData(frameLayoutElement),
    CHILDREN_PATH
  );
  const existingFrames = existingChildren
    ? Expressions.getList(existingChildren) || []
    : [];

  // Reuse existing grid frames (matched by deterministic id) so user
  // resizes / background edits survive re-evals and re-syncs.
  const existingFramesById = new Map<string, Expression>();
  for (const frame of existingFrames) {
    const frameElement = Expressions.getElement(frame);
    if (frameElement) existingFramesById.set(frameElement.id, frame);
  }

  // Inherit size / background from whichever frame is currently rendering
  // (typically the base variant frame, or a grid frame from the previous
  // sync). This keeps the canvas dimensions stable instead of resetting.
  const sourceFrame = existingFrames[0] ?? null;
  const sourceFrameElement = sourceFrame
    ? Expressions.getElement(sourceFrame)
    : null;
  const sourceFrameData = sourceFrameElement
    ? Expressions.getElementData(sourceFrameElement)
    : null;
  const sourceFrameSize = sourceFrameData
    ? Expressions.getIn(sourceFrameData, [{in: 'Record', at: 'size'}])
    : null;
  const sourceFrameBackground = sourceFrameData
    ? Expressions.getIn(sourceFrameData, [{in: 'Record', at: 'background'}])
    : null;

  const gridFrames: Expression[] = [];
  // variant-major, scenario-minor (see function docblock).
  for (const {variantId, frameId, instanceId} of getGridCells(
    componentId,
    variantIds,
    scenarios
  )) {
    const existingFrame = existingFramesById.get(frameId);
    if (existingFrame) {
      gridFrames.push(existingFrame);
      continue;
    }

    const instanceData =
      variantId && variantGroupId
        ? {
            [variantGroupId]: ELiteral({
              name: qualify(Plugins.name('Basic'), typeName('StyleVariant')),
              value: variantId,
            }),
          }
        : {};

    const componentInstance = EElement({
      id: instanceId,
      type: qualify(Plugins.name('__SitePlugin'), Component.name(componentId)),
      data: ERecord(instanceData),
    });

    gridFrames.push(
      EElement({
        id: frameId,
        type: qualify(Plugins.name('Frame'), Component.name('Frame')),
        data: ERecord({
          children: EList([componentInstance]),
          ...(sourceFrameSize ? {size: sourceFrameSize} : {}),
          ...(sourceFrameBackground ? {background: sourceFrameBackground} : {}),
        }),
      })
    );
  }

  return updatePageWithChildren(
    gridFrames,
    frameLayoutElement,
    pageElement,
    pageRender,
    componentMap
  );
}

export const getComponentWithChangedVariantsFromOperation = (
  prevComponents: Component.ComponentMap,
  nextComponents: Component.ComponentMap,
  operation: ComponentMapOperation
) => {
  if (operation.type !== 'componentUpdated') return null;
  const {componentName} = operation;
  const updatedComponent = nextComponents.get(componentName);
  const originalComponent = prevComponents.get(componentName);
  if (!updatedComponent || !originalComponent) return null;

  const updatedVariants = Component.getVariants(updatedComponent);
  const originalVariants = Component.getVariants(originalComponent);

  return updatedVariants === originalVariants ? null : componentName;
};
